import { useCallback, useEffect, useState } from 'react';
import { accountingGateway } from '../index';
import { glCategoryLabels } from '../types';
import type { GlExportResponse, PayrollRunSummary, ReconciliationResponse } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

export function GlExportsPage() {
  const [runs, setRuns] = useState<PayrollRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [exports, setExports] = useState<GlExportResponse[]>([]);
  const [reconciliations, setReconciliations] = useState<ReconciliationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const approved = await accountingGateway.listApprovedPayrollRuns();
      setRuns(approved);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load approved payroll runs.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (runId: string) => {
    setError(null);
    try {
      const [runExports, runReconciliations] = await Promise.all([
        accountingGateway.listGlExports(runId),
        accountingGateway.listReconciliations(runId),
      ]);
      setExports(runExports);
      setReconciliations(runReconciliations);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load GL export history.'));
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    if (selectedRunId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void loadDetail(selectedRunId);
    }
  }, [selectedRunId, loadDetail]);

  async function handleExport() {
    if (!selectedRunId) return;
    setError(null);
    setBusy(true);
    try {
      await accountingGateway.exportToGl(selectedRunId);
      await loadDetail(selectedRunId);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not export to GL.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleReconcile() {
    if (!selectedRunId) return;
    setError(null);
    setBusy(true);
    try {
      await accountingGateway.reconcile(selectedRunId);
      await loadDetail(selectedRunId);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not reconcile.'));
    } finally {
      setBusy(false);
    }
  }

  const latestExport = exports[0] ?? null;
  const latestReconciliation = reconciliations[0] ?? null;

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>GL Exports & Reconciliation</h2>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading approved payroll runs…</p>}
      {!loading && runs.length === 0 && <p className="muted">No Approved payroll runs yet.</p>}

      {!loading && runs.length > 0 && (
        <table>
          <thead>
            <tr><th>Period</th><th>Check Date</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id}>
                <td>{r.periodStartDate} – {r.periodEndDate}</td>
                <td>{r.checkDate}</td>
                <td>{r.status}</td>
                <td>
                  <button type="button" className="link" onClick={() => setSelectedRunId(r.id)}>
                    {selectedRunId === r.id ? 'Selected' : 'Select'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedRunId && (
        <div className="section">
          <div className="section-header">
            <h3>Payroll Run {selectedRunId}</h3>
            <div>
              <button type="button" onClick={() => void handleExport()} disabled={busy || latestExport !== null}>
                {latestExport ? 'Already exported' : busy ? 'Exporting…' : 'Export to GL'}
              </button>{' '}
              <button type="button" onClick={() => void handleReconcile()} disabled={busy || latestExport === null}>
                {busy ? 'Reconciling…' : 'Reconcile'}
              </button>
            </div>
          </div>

          {latestExport && (
            <>
              <p>
                Total debits: <strong>{latestExport.totalDebits.toFixed(2)}</strong> — Total credits:{' '}
                <strong>{latestExport.totalCredits.toFixed(2)}</strong>{' '}
                {latestExport.totalDebits === latestExport.totalCredits ? (
                  <span className="success">Balanced</span>
                ) : (
                  <span className="error">Out of balance</span>
                )}
              </p>
              <table>
                <thead>
                  <tr><th>Category</th><th>GL Account</th><th>Debit</th><th>Credit</th></tr>
                </thead>
                <tbody>
                  {latestExport.lines.map((line) => (
                    <tr key={line.category}>
                      <td>{glCategoryLabels[line.category]}</td>
                      <td>{line.glAccountCode} — {line.glAccountName}</td>
                      <td>{line.isDebit ? line.amount.toFixed(2) : ''}</td>
                      <td>{!line.isDebit ? line.amount.toFixed(2) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {latestReconciliation && (
            <p>
              Reconciliation: <strong>{latestReconciliation.status}</strong> — expected{' '}
              {latestReconciliation.expectedNetPayAmount.toFixed(2)}, actual{' '}
              {latestReconciliation.actualSettledAmount?.toFixed(2) ?? 'n/a'}
              {latestReconciliation.varianceAmount !== null && ` (variance ${latestReconciliation.varianceAmount.toFixed(2)})`}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
